import axios from "axios";
import { emitDataDateChanged, emitScheduleMutated } from "./scheduleMutations";
import { downloadBlobResponse } from "../utils/download";
import type {
  WBSTreeNode,
  WBSActivity,
  V2Schedule,
  ProjectStatus,
  ScheduleTable,
  ScheduleTableParams,
  StatusReviewResponse,
  AutoActualsResponse,
  F9SyncStatus,
  TaktBoardResponse,
  Trade,
  LogicTreeNode,
  PullPlanWBSNode,
  PullPlanSectionContext,
  ExistingWorkCode,
  WorkCodeCandidates,
  DbVersionInfo,
  DiffVersion,
  GroupedDiff,
  TreeDiff,
  SurveyTreeResponse,
  SingleResponseResult,
  ChangeEvent,
  DependencyGraph,
  PullPlan,
  PullPlanSnapshot,
  PullPlanSummary,
  PullPlanTaktSectionRef,
  CreatePullPlanBody,
  CreatePullPlanResponse,
  SavePullPlanBody,
  ListPullPlansParams,
  SetLeafTaktBody,
  SetLeafTaktResult,
  TaktSectionDraftRef,
  ScopeMember,
  EntryInsights,
  PublishPreview,
  PublishResult,
  DataDateResponse,
  WorkbenchBody,
  WorkbenchResult,
  ActivitySearchResult,
  DepGraph,
  DraftPayload,
  TaktEditSummary,
  TaktEditCommitResult,
  UntaggedGroupsResult,
  TaktNodeMapNode,
  SetTaktNodeMappingBody,
  SetTaktNodeMappingResult,
  BootstrapNodeMapResult,
  BulkSetNodeMappingResult,
} from "../models";

const API_V2_BASE =
  process.env.REACT_APP_API_V2_URL || "";

const clientV2 = axios.create({
  baseURL: API_V2_BASE,
  withCredentials: true,
});

// Telemetry posts bypass the interceptors: an event capture must not trigger
// the scheduleMutated refetch fan-out, and a background telemetry 401 must not
// force the page reload the error interceptor does.
const telemetryClient = axios.create({
  baseURL: API_V2_BASE,
  withCredentials: true,
});

clientV2.interceptors.response.use(
  (response) => {
    // Any successful write (not a preview, not auth) may have moved schedule
    // dates — notify subscribers so derived UI (header project-delta chip)
    // refetches. GETs and *-preview endpoints don't mutate, so skip them.
    const method = response.config.method?.toLowerCase();
    const url = response.config.url || "";
    if (
      (method === "post" || method === "delete" || method === "put") &&
      !url.includes("preview") &&
      !url.includes("/auth/")
    ) {
      emitScheduleMutated();
    }
    return response;
  },
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes("/auth/login") &&
      !error.config?.url?.includes("/auth/me")
    ) {
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export async function fetchV2Schedules(): Promise<V2Schedule[]> {
  const { data } = await clientV2.get("/api/v2/schedules");
  return data;
}

export async function fetchWBSRoots(
  scheduleId: number
): Promise<WBSTreeNode[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/wbs/roots`
  );
  return data;
}

export async function fetchWBSChildren(
  nodeId: number
): Promise<WBSTreeNode[]> {
  const { data } = await clientV2.get(`/api/v2/wbs/${nodeId}/children`);
  return data;
}

export async function fetchWBSActivities(
  nodeId: number
): Promise<WBSActivity[]> {
  const { data } = await clientV2.get(`/api/v2/wbs/${nodeId}/activities`);
  return data;
}

export async function fetchWBSFullTree(
  scheduleId: number
): Promise<WBSTreeNode[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/wbs/tree`
  );
  return data;
}

export async function fetchScheduleTable(
  scheduleId: number,
  opts: ScheduleTableParams = {}
): Promise<ScheduleTable> {
  const params: Record<string, string> = {};
  if (opts.wbsNodeId != null) params.wbs_node_id = String(opts.wbsNodeId);
  if (opts.cursor != null) params.cursor = String(opts.cursor);
  if (opts.limit != null) params.limit = String(opts.limit);
  if (opts.q) params.q = opts.q;
  if (opts.sortBy) params.sort_by = opts.sortBy;
  if (opts.sortDir) params.sort_dir = opts.sortDir;
  if (opts.excludeCompleted) params.exclude_completed = "true";
  if (opts.criticalOnly) params.critical_only = "true";
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/schedule-table`,
    { params }
  );
  return data;
}

// --- Logic tab ---

export async function fetchLogicTree(
  scheduleId: number,
  wbsNodeId?: number | null
): Promise<LogicTreeNode> {
  const params: Record<string, string> = {};
  if (wbsNodeId != null) params.wbs_node_id = String(wbsNodeId);
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/logic-tree`,
    { params }
  );
  return data;
}

export async function searchActivities(
  scheduleId: number,
  q: string,
  limit = 25
): Promise<ActivitySearchResult[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/activities/search`,
    { params: { q, limit } }
  );
  return data;
}

// --- Pull Plan ---

export async function fetchPullPlanWBSTree(
  scheduleId: number
): Promise<PullPlanWBSNode[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/wbs-tree`
  );
  return data;
}

export async function fetchPullPlanSectionContext(
  scheduleId: number,
  wbsNodeId: number
): Promise<PullPlanSectionContext> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/section/${wbsNodeId}/context`
  );
  return data;
}

export async function fetchPullPlanTaktSectionContext(
  scheduleId: number,
  sectionName: string,
): Promise<PullPlanSectionContext> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/takt-section-context`,
    { params: { name: sectionName } },
  );
  return data;
}

export async function fetchPullPlanTaktSections(
  scheduleId: number,
): Promise<PullPlanTaktSectionRef[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/takt-sections`,
  );
  return data.sections ?? [];
}

export async function previewTaktEdits(
  scheduleId: number,
  payload: object,
): Promise<TaktEditSummary> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-editor/preview`,
    payload,
  );
  return data;
}

export async function commitTaktEdits(
  scheduleId: number,
  payload: object,
): Promise<TaktEditCommitResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-editor/commit`,
    payload,
  );
  return data;
}

export async function fetchTaktNodeMapTree(
  scheduleId: number,
): Promise<TaktNodeMapNode[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/takt-node-map/tree`,
  );
  return data.nodes ?? [];
}

export async function setTaktNodeMapping(
  scheduleId: number,
  body: SetTaktNodeMappingBody,
): Promise<SetTaktNodeMappingResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-node-map/set`,
    body,
  );
  return data;
}

export async function setTaktNodeMappingBulk(
  scheduleId: number,
  edits: SetTaktNodeMappingBody[],
): Promise<BulkSetNodeMappingResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-node-map/set-bulk`,
    { edits },
  );
  return data;
}

export async function bootstrapTaktNodeMap(
  scheduleId: number,
): Promise<BootstrapNodeMapResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-node-map/bootstrap`,
    {},
  );
  return data;
}

export async function groupUntaggedActivities(
  scheduleId: number,
  activityIds: number[],
  stagedZones?: Array<{
    activity_id: number;
    section_name?: string;
    takt_a?: string;
    takt_b?: string;
    takt_c?: string;
  }>,
  excludedIds?: number[],
): Promise<UntaggedGroupsResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/takt-editor/group-untagged`,
    {
      activity_ids: activityIds,
      staged_zones: stagedZones ?? [],
      excluded_ids: excludedIds ?? [],
    },
  );
  return data;
}

// --- Add Work Code modal -------------------------------------------------

export async function fetchExistingWorkCodes(
  scheduleId: number,
  q?: string | null,
): Promise<ExistingWorkCode[]> {
  const params: Record<string, string> = {};
  if (q) params.q = q;
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/work-codes/existing`,
    { params },
  );
  return data;
}

export async function fetchWorkCodeCandidates(
  scheduleId: number,
  opts: { name?: string | null; code?: string | null } = {},
): Promise<WorkCodeCandidates> {
  const params: Record<string, string> = {};
  if (opts.name) params.name = opts.name;
  if (opts.code) params.code = opts.code;
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/work-codes/candidates`,
    { params },
  );
  return data;
}

export async function fetchNextWorkCode(scheduleId: number): Promise<string> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/work-codes/next-code`,
  );
  return data.code;
}

export async function fetchTaktBoard(
  scheduleId: number
): Promise<TaktBoardResponse> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/takt`
  );
  return data;
}

export async function fetchTaktSectionDependencyGraph(
  scheduleId: number,
  sectionName: string,
): Promise<DependencyGraph> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/takt-sections/${encodeURIComponent(sectionName)}/dependency-graph`,
  );
  return data;
}


// ---------------------------------------------------------------------------
// Pull-plan draft CRUD (services/pull_plan/drafts.py)
// ---------------------------------------------------------------------------

export async function listPullPlans(
  scheduleId: number,
  params: ListPullPlansParams = {},
): Promise<PullPlanSummary[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plans`,
    { params },
  );
  return data;
}

export async function fetchLatestDraftsByTaktSection(
  scheduleId: number,
): Promise<Record<string, TaktSectionDraftRef>> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plans/by-takt-section`,
  );
  return data;
}

export async function createPullPlan(
  scheduleId: number,
  body: CreatePullPlanBody,
): Promise<CreatePullPlanResponse> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/pull-plans`,
    body,
  );
  return data;
}

export async function fetchPullEntryInsights(
  scheduleId: number,
  wbsIds: number[],
): Promise<EntryInsights> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/entry-insights`,
    { params: { wbs_ids: wbsIds.join(",") } },
  );
  return data;
}

export async function fetchPullPlanScopeMembers(
  scheduleId: number,
  wbsIds: number[],
): Promise<ScopeMember[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plan/scope-members`,
    { params: { wbs_ids: wbsIds.join(",") } },
  );
  return data.members ?? [];
}

export async function setLeafTakt(
  scheduleId: number,
  wbsNodeId: number,
  body: SetLeafTaktBody,
): Promise<SetLeafTaktResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/pull-plan/sections/${wbsNodeId}/takt`,
    body,
  );
  return data;
}

export async function getPullPlan(
  scheduleId: number,
  pullPlanId: number,
): Promise<PullPlan> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}`,
  );
  return data;
}

export async function fetchPullPlanSnapshot(
  scheduleId: number,
  pullPlanId: number,
): Promise<PullPlanSnapshot> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}/snapshot`,
  );
  return data;
}

export async function savePullPlan(
  scheduleId: number,
  pullPlanId: number,
  body: SavePullPlanBody,
): Promise<PullPlan> {
  const { data } = await clientV2.put(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}`,
    body,
  );
  return data;
}

export async function discardPullPlan(
  scheduleId: number,
  pullPlanId: number,
  reason?: string,
): Promise<PullPlan> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}/discard`,
    reason !== undefined ? { reason } : {},
  );
  return data;
}

export async function deletePullPlan(
  scheduleId: number,
  pullPlanId: number,
): Promise<void> {
  await clientV2.delete(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}`,
  );
}

export async function previewSectionPublish(
  scheduleId: number,
  pullPlanId: number,
): Promise<PublishPreview> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}/preview-publish`,
  );
  return data;
}

export async function publishSection(
  scheduleId: number,
  pullPlanId: number,
  opts?: { changedBy?: string; applyDownstream?: boolean },
): Promise<PublishResult> {
  const body: Record<string, unknown> = {};
  if (opts?.changedBy !== undefined) body.changed_by = opts.changedBy;
  // Omit when true — the backend defaults to applying the full cascade.
  if (opts?.applyDownstream === false) body.apply_downstream = false;
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/pull-plans/${pullPlanId}/publish`,
    body,
  );
  return data;
}

export async function fetchTaktSectionWbsNodeIds(
  scheduleId: number,
  sectionName: string,
): Promise<number[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/takt-sections/${encodeURIComponent(sectionName)}/wbs-node-ids`,
  );
  return (data?.wbs_node_ids ?? []) as number[];
}

// --- Version management ---

export async function fetchDbVersions(): Promise<DbVersionInfo[]> {
  const { data } = await clientV2.get("/api/v2/versions");
  return data;
}

export async function fetchActiveVersion(): Promise<{ version: number }> {
  const { data } = await clientV2.get("/api/v2/active-version");
  return data;
}

export async function setActiveVersion(
  version: number
): Promise<{ version: number }> {
  const { data } = await clientV2.post("/api/v2/active-version", { version });
  return data;
}

export async function importXer(
  file: File,
  name?: string
): Promise<DbVersionInfo> {
  const form = new FormData();
  form.append("file", file);
  if (name) form.append("name", name);
  const { data } = await clientV2.post("/api/v2/import", form);
  return data;
}

// --- Diff ---

export async function fetchDiffVersions(): Promise<DiffVersion[]> {
  const { data } = await clientV2.get("/api/v2/diff/versions");
  return data;
}

export async function fetchDiff(
  oldVer: string,
  newVer: string
): Promise<GroupedDiff> {
  const { data } = await clientV2.get(
    `/api/v2/diff?old=${oldVer}&new=${newVer}`
  );
  return data;
}

// --- Survey ---

export async function fetchSurveyEntries(
  scheduleId: number,
  surveyDate: string
): Promise<SurveyTreeResponse> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/survey/${surveyDate}`
  );
  return data;
}

export async function respondSingle(
  scheduleId: number,
  surveyDate: string,
  activityId: number,
  category: string,
  happened: boolean
): Promise<SingleResponseResult> {
  const body: Record<string, unknown> = { activity_id: activityId, category, happened };
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/survey/${surveyDate}/respond`,
    body
  );
  return data;
}

export async function undoSurveyResponse(
  scheduleId: number,
  surveyDate: string,
  activityId: number
): Promise<{ total_responded: number; total_entries: number }> {
  const { data } = await clientV2.delete(
    `/api/v2/schedules/${scheduleId}/survey/${surveyDate}/respond/${activityId}`
  );
  return data;
}

export async function resetSurvey(
  scheduleId: number,
  surveyDate: string
): Promise<{ total_responded: number; total_entries: number }> {
  const { data } = await clientV2.delete(
    `/api/v2/schedules/${scheduleId}/survey/${surveyDate}`
  );
  return data;
}

// --- Mutations ---

export async function activityStart(
  scheduleId: number,
  activityId: number,
  actDate?: string,
  source?: string
): Promise<{ changes: Array<{ field: string; old: string; new: string }> }> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/activities/${activityId}/start`,
    { ...(actDate ? { date: actDate } : {}), ...(source ? { source } : {}) }
  );
  return data;
}

export async function activityComplete(
  scheduleId: number,
  activityId: number,
  actDate?: string,
  source?: string
): Promise<{ changes: Array<{ field: string; old: string; new: string }> }> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/activities/${activityId}/complete`,
    { ...(actDate ? { date: actDate } : {}), ...(source ? { source } : {}) }
  );
  return data;
}

export async function f9StatusReview(
  scheduleId: number
): Promise<StatusReviewResponse> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/f9/status-review`
  );
  return data;
}

export async function f9AutoActuals(
  scheduleId: number
): Promise<AutoActualsResponse> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/f9/auto-actuals`
  );
  return data;
}

/** Is the schedule reconciled with the forward pass? Cached server-side by a
 *  change_log watermark — cheap to poll after mutations. `force` bypasses the
 *  cache and recomputes (used when the F9 modal opens). */
export async function fetchF9SyncStatus(
  scheduleId: number,
  force = false
): Promise<F9SyncStatus> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/f9/sync-status`,
    force ? { params: { force: true } } : undefined
  );
  return data;
}

/** Complete {activity_id: work_code} for the whole schedule (the takt board only
 *  covers its own rows). Used to color every bar in the full-schedule F9 cascade. */
export async function fetchActivityWorkCodes(
  scheduleId: number
): Promise<Record<string, string>> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/work-codes`
  );
  return data;
}

export async function workbenchPreview(
  scheduleId: number,
  body: WorkbenchBody
): Promise<WorkbenchResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/workbench/preview`,
    body
  );
  return data;
}

export async function workbenchApply(
  scheduleId: number,
  body: WorkbenchBody
): Promise<WorkbenchResult> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/workbench/apply`,
    body
  );
  return data;
}

export async function fetchChangelog(
  scheduleId: number
): Promise<ChangeEvent[]> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/changelog`
  );
  return data;
}

export async function rollbackChange(
  scheduleId: number,
  changeId: number
): Promise<{ activity_id: number; field: string; restored_value: string }> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/changelog/${changeId}/rollback`
  );
  return data;
}

export async function rollbackBatch(
  scheduleId: number,
  batchId: string
): Promise<{
  rollback_batch_id: string;
  reverted_count: number;
  skipped_count: number;
}> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/changelog/batch/${batchId}/rollback`
  );
  return data;
}

// --- Working diff (mutations) ---

export async function fetchWorkingDiff(
  version?: number
): Promise<GroupedDiff> {
  const params = version != null ? `?version=${version}` : "";
  const { data } = await clientV2.get(`/api/v2/diff/working${params}`);
  return data;
}

// --- XER Export ---

export function getExportXerUrl(scheduleId: number): string {
  return `${API_V2_BASE}/api/v2/schedules/${scheduleId}/export/xer`;
}

// --- Takt PDF Export ---

/** The default title the lookahead export would use, to seed the editable
 *  title field in the export dialog. */
export async function fetchTaktExportTitle(scheduleId: number): Promise<string> {
  const response = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/export/takt-pdf/title`
  );
  return response.data?.title ?? "";
}

export async function exportTaktPdf(
  scheduleId: number,
  startDate: string,
  numWeeks: number,
  displayType: "detailed" | "overview" = "detailed",
  tradeIds: number[] = [],
  tradeNames: string[] = [],
  includeUnassigned: boolean = true,
  pageSize: string = "11x17",
  customWidthIn?: number,
  customHeightIn?: number,
  showNames: boolean = false,
  title?: string
): Promise<void> {
  const response = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/export/takt-pdf`,
    {
      start_date: startDate,
      num_weeks: numWeeks,
      display_type: displayType,
      trade_ids: tradeIds,
      trade_names: tradeNames,
      include_unassigned: includeUnassigned,
      page_size: pageSize,
      custom_width_in: customWidthIn,
      custom_height_in: customHeightIn,
      show_names: showNames,
      title: title,
    },
    { responseType: "blob" }
  );
  downloadBlobResponse(response, `Schedule_Update_${startDate}_${numWeeks}wk.pdf`);
}

// --- Schedule Status PDF Export ---

export async function exportStatusPdf(scheduleId: number): Promise<void> {
  const response = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/export/status-pdf`,
    {},
    { responseType: "blob" }
  );
  downloadBlobResponse(response, "Schedule_Status.pdf");
}

// --- Diff revert ---

export async function revertDiffField(
  oldVer: string,
  newVer: string,
  taskCode: string,
  fields?: string[]
): Promise<{ changes: Array<{ field: string; old_value: string; new_value: string }>; task_code: string }> {
  const body: Record<string, unknown> = { old_ver: oldVer, new_ver: newVer, task_code: taskCode };
  if (fields) body.fields = fields;
  const { data } = await clientV2.post("/api/v2/diff/revert", body);
  return data;
}

export async function revertAllDiff(
  oldVer: string,
  newVer: string
): Promise<{ reverted_count: number; activities: Array<{ task_code: string; changes: Array<{ field: string; old_value: string; new_value: string }> }> }> {
  const { data } = await clientV2.post("/api/v2/diff/revert-all", { old_ver: oldVer, new_ver: newVer });
  return data;
}

// --- Trades ---
// Trade CRUD + assignments are no longer written directly from the client —
// they stage in the Takt Editor's edit buffer and commit through
// POST /takt-editor/commit. Only the read remains here.

export async function fetchTrades(): Promise<Trade[]> {
  const { data } = await clientV2.get("/api/v2/trades");
  return data;
}

export async function fetchDiffTree(
  oldVer: string,
  newVer: string
): Promise<TreeDiff> {
  const url = `/api/v2/diff/tree?old=${oldVer}&new=${newVer}`;
  const { data } = await clientV2.get(url);
  return data;
}

export async function setDataDate(
  scheduleId: number,
  targetDate: string,
): Promise<DataDateResponse> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/data-date`,
    { data_date: targetDate },
  );
  // Idempotent same-date sets write nothing — don't make data-date-relative
  // views refetch (and wipe staged edits) for a change that didn't happen.
  if (data.changed) emitDataDateChanged();
  return data;
}

/** Rename a schedule's display label. Edits Schedule.name only — the P6
 *  project name from the imported XER is preserved. */
export async function renameSchedule(
  scheduleId: number,
  name: string,
): Promise<{ id: number; name: string }> {
  const { data } = await clientV2.patch(
    `/api/v2/schedules/${scheduleId}`,
    { name },
  );
  return data;
}

/** ONE flat scheduled-graph read for both dep editors. `section` is the
 *  Takt_SectionName; `draft` is the pull-plan payload (pending_* arrays) so
 *  staged edits show live before publish. The Takt board omits `draft` and
 *  gets the live committed graph. Section travels in the body so it never needs
 *  URL escaping. */
export async function fetchDepEditorGraph(
  scheduleId: number,
  sectionName: string,
  draft?: DraftPayload,
): Promise<DepGraph> {
  const { data } = await clientV2.post(
    `/api/v2/schedules/${scheduleId}/dep-editor/graph`,
    draft !== undefined
      ? { section: sectionName, draft }
      : { section: sectionName },
  );
  return data;
}

export async function fetchProjectStatus(
  scheduleId: number,
): Promise<ProjectStatus> {
  const { data } = await clientV2.get(
    `/api/v2/schedules/${scheduleId}/project-status`,
  );
  return data;
}

/** Fire a frontend product (PostHog) event through the backend capture path.
 *  Best-effort: telemetry must never disrupt the UI, so failures are swallowed. */
export async function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
): Promise<void> {
  try {
    await telemetryClient.post("/api/v2/events", { event, properties });
  } catch {
    /* swallow — telemetry must not break the app */
  }
}
